/**
 * Closed vocabularies for `AlertStyle`/`AlertLayout` fields that become CSS
 * class tokens (`packages/alerts/src/style.ts`, `@connaro/overlay-style`).
 *
 * Duplicated here rather than imported: `apps/web` has no `@connaro/*`
 * dependency (see the same note on `pages/alerts/preview.ts`) — the Console
 * is an HTTP client, and the API is the authority. `alertStyleSchema` and
 * `overlayStyleSchema` bound these same lists on WRITE; `pickEnum` below is
 * what bounds them on READ, which is a separate and necessary check — the
 * column is `jsonb(...).$type<Record<string, unknown>>()` at rest, a
 * compile-time cast with no runtime constraint, so a row can hold anything.
 */
export const ALERT_THEMES = ['minimal', 'dark', 'light', 'neon'] as const;
export const ALERT_FONTS = ['system', 'sans', 'serif', 'mono', 'wide'] as const;
export const ALERT_COLORS = ['white', 'amber', 'green', 'cyan', 'violet', 'pink', 'red'] as const;
export const ALERT_ANIMATIONS = ['none', 'fade', 'slide', 'pop'] as const;
export const ALERT_POSITIONS = [
  'top-left', 'top-center', 'top-right',
  'middle-left', 'center', 'middle-right',
  'bottom-left', 'bottom-center', 'bottom-right',
] as const;

/**
 * Bounds an untrusted style/layout value to one of `allowed` before it
 * becomes a CSS class token, falling back exactly as a failed `typeof` check
 * already did.
 *
 * `typeof value === 'string'` alone is not enough: a value outside the
 * closed list — including a string containing a SPACE — would otherwise be
 * interpolated bare into a class name and smuggle a second, unconfigured
 * class token in alongside the first (e.g. `theme: "x overlay__alert--
 * outline"` silently turning outline on). Membership in the list this field
 * is actually drawn from closes that, the same way `WidgetRender`'s `align`
 * handling does.
 */
export function pickEnum<T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T,
): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}
